import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import Bowser from 'bowser';
import { serializeError } from 'serialize-error';

/** Request-body keys whose values are never sent, even without removeData */
const SECRET_KEY = /pass(word)?|ssn|secret|token/i;

/**
 * Masks secret values in a JSON request body (the string Amplify sends), so
 * a call that forgets `removeData` still doesn't report a password.
 * Anything that isn't JSON is returned unchanged.
 */
const redactSecrets = (data: unknown) => {
  if (typeof data !== 'string') {
    return data;
  }
  try {
    return JSON.stringify(JSON.parse(data), (key, value) =>
      key && SECRET_KEY.test(key) ? '[redacted]' : value,
    );
  } catch (error) {
    return data;
  }
};

/**
 * Reports an error to the alerts endpoint (production) or the console.
 * Request headers are always dropped and secret-looking body fields masked.
 *
 * @param removeData - Drop the request entirely (body, query string): use
 *   it for any call that sends a password, temporary password, SSN or
 *   payment details
 */
export async function captureError(params: {
  data: any;
  removeData?: boolean;
}) {
  const body: any = {};

  if (params.data) {
    body.data = params.data;
    if (params.data.error) {
      // create a copy of params.data.error before its serialized
      const newError = params.data.error;
      // serializeError will mutate the error passed in params.data.error by calling toJSON on the axios error
      body.data.error = serializeError(params.data.error);
      // Remove error config headers
      if (body.data.error?.config?.headers) {
        delete body.data.error.config.headers;
      }
      // Remove error response config headers
      if (body.data.error?.response?.config?.headers) {
        delete body.data.error.response.config.headers;
      }
      // Remove error response headers
      if (body.data.error?.response?.headers) {
        delete body.data.error.response.headers;
      }

      const configs = [
        body.data.error?.config,
        body.data.error?.response?.config,
      ].filter(Boolean);
      configs.forEach((config) => {
        if (params.removeData) {
          // Remove the request: body (signed requests copy it to `body`),
          // query parameters and the query string
          delete config.data;
          delete config.body;
          delete config.params;
          if (typeof config.url === 'string') {
            config.url = config.url.split('?')[0];
          }
        } else {
          config.data = redactSecrets(config.data);
        }
      });

      if (newError?.response?.data?.errors) {
        // If the API has returned an errors object like in the case of a 400 response
        // serialize and add it to the body data
        body.data.errors = serializeError(newError.response.data.errors);
      }
    }
    body.data.window = {};
    if (window.navigator && window.navigator.userAgent) {
      body.data.window.userAgent = window.navigator.userAgent;
      // Browser Info
      body.data.client = Bowser.parse(window.navigator.userAgent);
    }
    if (window.location && window.location.href) {
      body.data.window.href = window.location.href;
    }
    if (process.env.REACT_APP_AWS_BRANCH) {
      body.data.AWS_BRANCH = process.env.REACT_APP_AWS_BRANCH;
    }
    // Current User
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      if (user) {
        body.data.username = user.getUsername();
      }
    } catch (error) {
      // No current user
    }
  }

  if (
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_ALERTS_URL
  ) {
    fetch(process.env.REACT_APP_ALERTS_URL, {
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
  } else {
    // What production would send, so secrets stay out of the console too
    console.error({ captureError: body });
  }
}
